import React, { useState, useEffect } from 'react';
import { Modal, Button, Label, TextInput, Select, Alert } from 'flowbite-react';
import { HiUserAdd, HiExclamation, HiCheck, HiX } from 'react-icons/hi';
import { staffApi, type DuplicateMatch, type StaffCreateWithPersonData } from '../../api/staff';

interface CreateStaffModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (staff: { staffId: number; personId: number }) => void;

  // Context-aware defaults
  defaultOrganizationId?: number;
  defaultRoleDescriptorId?: number;  // For board members
  defaultPositionTitle?: string;      // For solicitors

  // Display context
  modalTitle?: string;  // "Add Staff Member" / "Add Board Member" / "Add Solicitor"
}

export const CreateStaffModal: React.FC<CreateStaffModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  defaultOrganizationId,
  defaultRoleDescriptorId: _defaultRoleDescriptorId,
  defaultPositionTitle: _defaultPositionTitle,
  modalTitle = "Add Staff Member"
}) => {
  const [step, setStep] = useState<'form' | 'duplicates'>('form');
  const [formData, setFormData] = useState<StaffCreateWithPersonData>({
    firstName: '',
    lastName: '',
    email: '',
    middleName: '',
    preferredName: '',
    honorific: '',
    isActive: true
  });
  const [duplicates, setDuplicates] = useState<DuplicateMatch[]>([]);
  const [_selectedExistingStaff, setSelectedExistingStaff] = useState<DuplicateMatch | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('form');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        middleName: '',
        preferredName: '',
        honorific: '',
        isActive: true
      });
      setDuplicates([]);
      setSelectedExistingStaff(null);
      setError(null);
      setFieldErrors({});
    }
  }, [isOpen]);

  // Validate email format
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate form fields
  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission - check for duplicates
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate form
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Check for duplicates
      const matches = await staffApi.checkDuplicates({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        organizationId: defaultOrganizationId
      });

      if (matches.length > 0) {
        // Found potential duplicates - show confirmation view
        setDuplicates(matches);
        setStep('duplicates');
      } else {
        // No duplicates - create directly
        await createStaff();
      }
    } catch (err: any) {
      // Handle different error formats
      let errorMessage = 'Failed to check for duplicates';

      // Check for ApiError response data first
      if (err?.response?.detail) {
        errorMessage = typeof err.response.detail === 'string'
          ? err.response.detail
          : JSON.stringify(err.response.detail, null, 2);
      } else if (err instanceof Error) {
        errorMessage = err.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      console.error('Duplicate check error:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Create new staff with person
  const createStaff = async () => {
    setLoading(true);
    setError(null);

    try {
      // Clean up form data - convert empty strings to undefined for optional fields
      const cleanedData = {
        ...formData,
        middleName: formData.middleName?.trim() || undefined,
        preferredName: formData.preferredName?.trim() || undefined,
        honorific: formData.honorific?.trim() || undefined,
        hireDate: formData.hireDate?.trim() || undefined,
      };

      const result = await staffApi.createWithPerson(cleanedData);

      // Success - call onSuccess with staff IDs
      onSuccess({
        staffId: result.staffId,
        personId: result.personId
      });

      // Close modal
      onClose();
    } catch (err: any) {
      // Handle different error formats
      let errorMessage = 'Failed to create staff member';

      // Check for ApiError response data first
      if (err?.response?.detail) {
        errorMessage = typeof err.response.detail === 'string'
          ? err.response.detail
          : JSON.stringify(err.response.detail, null, 2);
      } else if (err instanceof Error) {
        errorMessage = err.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      console.error('Create staff error:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle selection of existing staff from duplicates
  const handleSelectExisting = (match: DuplicateMatch) => {
    // Call onSuccess with existing staff
    onSuccess({
      staffId: match.staffId,
      personId: match.personId
    });

    // Close modal
    onClose();
  };

  // Handle "None of these, create new" button
  const handleCreateNewAnyway = async () => {
    await createStaff();
  };

  // Render confidence badge
  const renderConfidenceBadge = (confidence: number) => {
    let color = 'gray';
    let label = 'Low';

    if (confidence >= 0.95) {
      color = 'red';
      label = 'Very High';
    } else if (confidence >= 0.85) {
      color = 'orange';
      label = 'High';
    } else if (confidence >= 0.70) {
      color = 'yellow';
      label = 'Medium';
    }

    return (
      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-${color}-100 text-${color}-800`}>
        {label} Match ({Math.round(confidence * 100)}%)
      </span>
    );
  };

  // Render form step
  const renderFormStep = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Required Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="firstName">
            First Name <span className="text-red-600">*</span>
          </Label>
          <TextInput
            id="firstName"
            type="text"
            value={formData.firstName}
            onChange={(e) => {
              setFormData({ ...formData, firstName: e.target.value });
              if (fieldErrors.firstName) {
                setFieldErrors({ ...fieldErrors, firstName: '' });
              }
            }}
            className={fieldErrors.firstName ? 'border-red-500' : ''}
            disabled={loading}
            aria-invalid={!!fieldErrors.firstName}
          />
          {fieldErrors.firstName && (
            <p className="text-sm text-red-600 mt-1">{fieldErrors.firstName}</p>
          )}
        </div>

        <div>
          <Label htmlFor="lastName">
            Last Name <span className="text-red-600">*</span>
          </Label>
          <TextInput
            id="lastName"
            type="text"
            value={formData.lastName}
            onChange={(e) => {
              setFormData({ ...formData, lastName: e.target.value });
              if (fieldErrors.lastName) {
                setFieldErrors({ ...fieldErrors, lastName: '' });
              }
            }}
            className={fieldErrors.lastName ? 'border-red-500' : ''}
            disabled={loading}
            aria-invalid={!!fieldErrors.lastName}
          />
          {fieldErrors.lastName && (
            <p className="text-sm text-red-600 mt-1">{fieldErrors.lastName}</p>
          )}
        </div>
      </div>

      <div>
        <Label htmlFor="email">
          Email Address <span className="text-red-600">*</span>
        </Label>
        <TextInput
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => {
            setFormData({ ...formData, email: e.target.value });
            if (fieldErrors.email) {
              setFieldErrors({ ...fieldErrors, email: '' });
            }
          }}
          className={fieldErrors.email ? 'border-red-500' : ''}
          disabled={loading}
          aria-invalid={!!fieldErrors.email}
        />
        {fieldErrors.email && (
          <p className="text-sm text-red-600 mt-1">{fieldErrors.email}</p>
        )}
      </div>

      {/* Optional Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="middleName">Middle Name</Label>
          <TextInput
            id="middleName"
            type="text"
            value={formData.middleName}
            onChange={(e) => setFormData({ ...formData, middleName: e.target.value })}
            disabled={loading}
          />
        </div>

        <div>
          <Label htmlFor="preferredName">Preferred Name</Label>
          <TextInput
            id="preferredName"
            type="text"
            value={formData.preferredName}
            onChange={(e) => setFormData({ ...formData, preferredName: e.target.value })}
            disabled={loading}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="honorific">Honorific</Label>
        <Select
          id="honorific"
          value={formData.honorific}
          onChange={(e) => setFormData({ ...formData, honorific: e.target.value })}
          disabled={loading}
        >
          <option value="">None</option>
          <option value="Dr.">Dr.</option>
          <option value="Mr.">Mr.</option>
          <option value="Mrs.">Mrs.</option>
          <option value="Ms.">Ms.</option>
          <option value="Prof.">Prof.</option>
        </Select>
      </div>

      {/* Info Box */}
      <div className="p-3 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-700">
          <span className="font-semibold">Note:</span> After creating the staff record, you'll be able to assign them to organizations and positions.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button color="gray" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Checking...' : 'Next'}
        </Button>
      </div>
    </form>
  );

  // Render duplicates confirmation step
  const renderDuplicatesStep = () => (
    <div className="space-y-4">
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex gap-3">
          <HiExclamation className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-amber-900 mb-1">
              Potential Duplicates Found
            </h4>
            <p className="text-sm text-amber-700">
              We found {duplicates.length} existing staff member{duplicates.length > 1 ? 's' : ''} with a similar name.
              Please confirm if any of these are the same person you're trying to add.
            </p>
          </div>
        </div>
      </div>

      {/* List of duplicate matches */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {duplicates.map((match) => (
          <div
            key={match.staffId}
            className="p-4 border rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
          >
            <div className="flex justify-between items-start mb-2">
              <div>
                <h4 className="font-semibold text-gray-900">
                  {match.displayName}
                </h4>
                {match.displayName !== `${match.firstName} ${match.lastName}` && (
                  <p className="text-sm text-gray-600">
                    Legal Name: {match.firstName} {match.middleName} {match.lastName}
                  </p>
                )}
              </div>
              {renderConfidenceBadge(match.matchConfidence)}
            </div>

            {/* Contact Info */}
            <div className="space-y-1 mb-3">
              {match.primaryEmail && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Email:</span> {match.primaryEmail}
                </p>
              )}
              {match.primaryPhone && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Phone:</span> {match.primaryPhone}
                </p>
              )}
            </div>

            {/* Current Assignments */}
            {match.assignments.length > 0 && (
              <div className="mb-3">
                <p className="text-sm font-medium text-gray-700 mb-1">Current Assignments:</p>
                <div className="space-y-1">
                  {match.assignments.map((assignment) => (
                    <div key={assignment.assignmentId} className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                      <span>
                        {assignment.positionTitle || assignment.roleName} - {assignment.organizationName}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Match Reasons */}
            <div className="flex flex-wrap gap-1 mb-3">
              {match.matchReasons.map((reason, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-600"
                >
                  {reason.replace(/_/g, ' ')}
                </span>
              ))}
            </div>

            {/* Action Button */}
            <Button
              size="sm"
              color="blue"
              onClick={() => handleSelectExisting(match)}
              className="w-full"
            >
              <HiCheck className="mr-2 h-4 w-4" />
              Yes, this is the same person
            </Button>
          </div>
        ))}
      </div>

      {/* Create New Button */}
      <div className="pt-4 border-t">
        <Button
          color="gray"
          onClick={handleCreateNewAnyway}
          disabled={loading}
          className="w-full"
        >
          <HiX className="mr-2 h-4 w-4" />
          None of these, create new person
        </Button>
      </div>

      {/* Back Button */}
      <div className="flex justify-between pt-2">
        <Button
          color="light"
          size="sm"
          onClick={() => setStep('form')}
          disabled={loading}
        >
          Back to Form
        </Button>
      </div>
    </div>
  );

  return (
    <Modal show={isOpen} onClose={onClose} dismissible size={step === 'duplicates' ? 'xl' : 'lg'}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <HiUserAdd className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900">
            {step === 'duplicates' ? 'Confirm Person Identity' : modalTitle}
          </h3>
        </div>

        {/* Error Message */}
        {error && (
          <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
            {error}
          </Alert>
        )}

        {/* Content */}
        {step === 'form' ? renderFormStep() : renderDuplicatesStep()}
      </div>
    </Modal>
  );
};
